/**
 * 🖥️ Modern HTML Preview Component
 * Sichere HTML-Vorschau mit Sandbox und modernem Design
 */

import React, { useState, useRef, useEffect } from 'react';
import Frame from 'react-frame-component';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { 
  Eye, 
  EyeOff, 
  Monitor, 
  Smartphone, 
  Tablet,
  Code,
  Maximize2,
  Minimize2,
  RefreshCw
} from 'lucide-react';

const HtmlPreview = ({ htmlContent, subject = '', isVisible = false, onToggle, userId = null }) => {
  const [viewMode, setViewMode] = useState('desktop'); // desktop, tablet, mobile
  const [showCode, setShowCode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cidLogos, setCidLogos] = useState([]);
  const frameRef = useRef(null);

  // Load CID logos for replacement
  useEffect(() => {
    const loadCidLogos = async () => {
      try {
        const response = await axios.get('/api/cid-logos');
        setCidLogos(response.data.data || []);
      } catch (error) {
        console.warn('Could not load CID logos for preview:', error);
        setCidLogos([]);
      }
    };

    if (htmlContent.includes('cid:')) {
      loadCidLogos();
    }
  }, [htmlContent]);

  // Replace CID references with actual image URLs
  const replaceCidReferences = (content) => {
    if (!content.includes('cid:') || cidLogos.length === 0) {
      return content;
    }

    let processedContent = content;
    
    // Replace each CID reference with the actual image URL
    cidLogos.forEach(logo => {
      const cidPattern = new RegExp(`cid:${logo.name}`, 'gi');
      if (logo.image_url) {
        processedContent = processedContent.replace(cidPattern, logo.image_url);
      }
    });

    return processedContent;
  };

  // Process and sanitize HTML content
  const processHtmlContent = (content) => {
    // First replace CID references
    let processedContent = replaceCidReferences(content);
    
    // Convert line breaks to <br> tags if not already HTML
    if (!/<[^>]+>/.test(processedContent)) {
      processedContent = processedContent
        .replace(/\n\n/g, '</p><p>')  // Double line breaks become paragraph breaks
        .replace(/\n/g, '<br>')       // Single line breaks become <br>
        .replace(/^/, '<p>')          // Add opening paragraph
        .replace(/$/, '</p>');        // Add closing paragraph
    }
    
    return processedContent;
  };

  const processedHtml = processHtmlContent(htmlContent);
  
  // Sanitize HTML content
  const sanitizedHtml = DOMPurify.sanitize(processedHtml, {
    ADD_TAGS: ['style', 'script'],
    ADD_ATTR: ['style', 'class', 'id', 'src', 'href', 'target', 'alt', 'title'],
    ALLOW_DATA_ATTR: true,
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
  });

  // Responsive dimensions
  const getFrameDimensions = () => {
    switch (viewMode) {
      case 'mobile':
        return { width: '375px', height: '667px' };
      case 'tablet':
        return { width: '768px', height: '1024px' };
      case 'desktop':
      default:
        return { width: '100%', height: '600px' };
    }
  };

  // Check if content is already a complete HTML document
  const isCompleteHtml = (content) => {
    return content.toLowerCase().includes('<!doctype') || 
           content.toLowerCase().includes('<html') ||
           (content.toLowerCase().includes('<head>') && content.toLowerCase().includes('<body>'));
  };

  // Create email template wrapper only if needed
  const createEmailTemplate = (content) => {
    // If it's already a complete HTML document, use it as-is
    if (isCompleteHtml(content)) {
      return content;
    }

    // Otherwise, wrap it in a comprehensive email template
    return `
      <!DOCTYPE html>
      <html lang="de">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${subject}</title>
        <style>
          /* Reset and base styles */
          * {
            box-sizing: border-box;
          }
          
          body {
            margin: 0;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            background-color: #f5f5f5;
            font-size: 16px;
          }
          
          .email-wrapper {
            max-width: 600px;
            margin: 0 auto;
            background: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          
          /* Typography */
          h1, h2, h3, h4, h5, h6 {
            margin: 0 0 16px 0;
            font-weight: 600;
            line-height: 1.3;
          }
          
          h1 { font-size: 32px; color: #1a1a1a; }
          h2 { font-size: 28px; color: #1a1a1a; }
          h3 { font-size: 24px; color: #1a1a1a; }
          h4 { font-size: 20px; color: #333; }
          h5 { font-size: 18px; color: #333; }
          h6 { font-size: 16px; color: #333; }
          
          p {
            margin: 0 0 16px 0;
            line-height: 1.6;
          }
          
          /* Text formatting */
          strong, b {
            font-weight: 600;
            color: #1a1a1a;
          }
          
          em, i {
            font-style: italic;
          }
          
          u {
            text-decoration: underline;
          }
          
          s, strike, del {
            text-decoration: line-through;
            color: #666;
          }
          
          mark {
            background-color: #fff3cd;
            padding: 2px 4px;
            border-radius: 2px;
          }
          
          small {
            font-size: 14px;
            color: #666;
          }
          
          sub {
            vertical-align: sub;
            font-size: 0.8em;
          }
          
          sup {
            vertical-align: super;
            font-size: 0.8em;
          }
          
          /* Text alignment */
          .text-left { text-align: left; }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .text-justify { text-align: justify; }
          
          /* Text colors */
          .text-primary { color: #0066cc; }
          .text-success { color: #28a745; }
          .text-warning { color: #ffc107; }
          .text-danger { color: #dc3545; }
          .text-info { color: #17a2b8; }
          .text-muted { color: #6c757d; }
          
          /* Font sizes */
          .text-xs { font-size: 12px; }
          .text-sm { font-size: 14px; }
          .text-lg { font-size: 18px; }
          .text-xl { font-size: 20px; }
          .text-2xl { font-size: 24px; }
          .text-3xl { font-size: 30px; }
          
          /* Links */
          a {
            color: #0066cc;
            text-decoration: none;
            border-bottom: 1px solid transparent;
            transition: all 0.2s ease;
          }
          
          a:hover {
            color: #0052a3;
            border-bottom-color: #0052a3;
          }
          
          /* Lists */
          ul, ol {
            margin: 0 0 16px 0;
            padding-left: 24px;
          }
          
          li {
            margin-bottom: 8px;
            line-height: 1.5;
          }
          
          /* Images */
          img {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
            vertical-align: middle;
          }
          
          /* Image alignment classes */
          img.left, .img-left img {
            float: left;
            margin: 0 16px 16px 0;
            display: inline-block;
          }
          
          img.right, .img-right img {
            float: right;
            margin: 0 0 16px 16px;
            display: inline-block;
          }
          
          img.center, .img-center img {
            display: block;
            margin: 16px auto;
          }
          
          /* Inline images */
          img.inline {
            display: inline-block;
            vertical-align: middle;
            margin: 0 4px;
          }
          
          /* Logo specific styles */
          .logo, img[src*="logo"] {
            max-height: 80px;
            width: auto;
          }
          
          /* Clear floats */
          .clearfix::after {
            content: "";
            display: table;
            clear: both;
          }
          
          /* Tables */
          table {
            border-collapse: collapse;
            width: 100%;
            margin: 16px 0;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          th, td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid #e5e5e5;
          }
          
          th {
            background-color: #f8f9fa;
            font-weight: 600;
            color: #333;
          }
          
          tr:hover {
            background-color: #f8f9fa;
          }
          
          /* Blockquotes */
          blockquote {
            margin: 16px 0;
            padding: 16px 20px;
            background-color: #f8f9fa;
            border-left: 4px solid #0066cc;
            font-style: italic;
            color: #555;
          }
          
          /* Code */
          code {
            background-color: #f1f3f4;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 14px;
            color: #d73a49;
          }
          
          pre {
            background-color: #f8f9fa;
            padding: 16px;
            border-radius: 8px;
            overflow-x: auto;
            margin: 16px 0;
          }
          
          pre code {
            background: none;
            padding: 0;
            color: #333;
          }
          
          /* Buttons */
          .btn, button {
            display: inline-block;
            padding: 12px 24px;
            background-color: #0066cc;
            color: white;
            text-decoration: none;
            border-radius: 6px;
            font-weight: 500;
            border: none;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }
          
          .btn:hover, button:hover {
            background-color: #0052a3;
            color: white;
          }
          
          /* Dividers */
          hr {
            border: none;
            height: 1px;
            background-color: #e5e5e5;
            margin: 24px 0;
          }
          
          /* Email layout helpers */
          .header {
            text-align: center;
            padding: 20px 0;
            border-bottom: 1px solid #e5e5e5;
            margin-bottom: 30px;
          }
          
          .footer {
            text-align: center;
            padding: 20px 0;
            border-top: 1px solid #e5e5e5;
            margin-top: 30px;
            font-size: 14px;
            color: #666;
          }
          
          .content-section {
            margin: 20px 0;
          }
          
          .signature {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e5e5e5;
            font-size: 14px;
          }
          
          /* Flexbox layouts for better positioning */
          .flex {
            display: flex;
            align-items: center;
          }
          
          .flex-column {
            display: flex;
            flex-direction: column;
          }
          
          .flex-center {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          
          .flex-between {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          /* Responsive */
          @media (max-width: 600px) {
            body {
              padding: 10px;
            }
            
            .email-wrapper {
              padding: 20px;
              border-radius: 4px;
            }
            
            h1 { font-size: 28px; }
            h2 { font-size: 24px; }
            h3 { font-size: 20px; }
            
            table {
              font-size: 14px;
            }
            
            th, td {
              padding: 8px 12px;
            }
            
            /* Mobile image adjustments */
            img.left, img.right {
              float: none;
              display: block;
              margin: 16px auto;
            }
            
            .flex {
              flex-direction: column;
            }
            
            .flex-between {
              flex-direction: column;
              align-items: center;
            }
          }
          
          /* Email client specific fixes */
          .ExternalClass {
            width: 100%;
          }
          
          .ExternalClass, .ExternalClass p, .ExternalClass span, .ExternalClass font, .ExternalClass td, .ExternalClass div {
            line-height: 100%;
          }
          
          /* Outlook fixes */
          table {
            mso-table-lspace: 0pt;
            mso-table-rspace: 0pt;
          }
          
          img {
            -ms-interpolation-mode: bicubic;
          }
        </style>
      </head>
      <body>
        <div class="email-wrapper">
          ${content}
        </div>
      </body>
      </html>
    `;
  };

  if (!isVisible) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <button
          onClick={onToggle}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <Eye className="h-5 w-5 mr-2" />
          HTML-Vorschau anzeigen
        </button>
      </div>
    );
  }

  const dimensions = getFrameDimensions();

  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
      {/* Preview Header */}
      <div className="bg-gray-50 border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onToggle}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <EyeOff className="h-5 w-5 mr-2" />
              Vorschau ausblenden
            </button>
            
            <div className="h-4 w-px bg-gray-300"></div>
            
            {/* View Mode Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('desktop')}
                className={`p-2 rounded ${viewMode === 'desktop' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
                title="Desktop-Ansicht"
              >
                <Monitor className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('tablet')}
                className={`p-2 rounded ${viewMode === 'tablet' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
                title="Tablet-Ansicht"
              >
                <Tablet className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('mobile')}
                className={`p-2 rounded ${viewMode === 'mobile' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
                title="Mobile-Ansicht"
              >
                <Smartphone className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowCode(!showCode)}
              className={`p-2 rounded ${showCode ? 'bg-green-100 text-green-600' : 'text-gray-600 hover:text-gray-900'}`}
              title="HTML-Code anzeigen"
            >
              <Code className="h-4 w-4" />
            </button>
            
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 rounded text-gray-600 hover:text-gray-900"
              title={isFullscreen ? 'Vollbild verlassen' : 'Vollbild'}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            
            <button
              onClick={() => {
                if (frameRef.current) {
                  frameRef.current.forceUpdate();
                }
              }}
              className="p-2 rounded text-gray-600 hover:text-gray-900"
              title="Aktualisieren"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {/* Device Info */}
        <div className="mt-2 text-sm text-gray-500">
          {viewMode === 'desktop' && 'Desktop-Ansicht (Responsive)'}
          {viewMode === 'tablet' && 'Tablet-Ansicht (768×1024)'}
          {viewMode === 'mobile' && 'Mobile-Ansicht (375×667)'}
        </div>
      </div>

      <div className="flex">
        {/* HTML Preview */}
        <div className={`${showCode ? 'w-1/2' : 'w-full'} bg-gray-100 p-4`}>
          <div className="flex justify-center">
            <div 
              className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300"
              style={{
                width: dimensions.width,
                height: dimensions.height,
                maxWidth: '100%'
              }}
            >
              <Frame
                ref={frameRef}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                initialContent={createEmailTemplate(sanitizedHtml)}
              />
            </div>
          </div>
        </div>

        {/* HTML Code View */}
        {showCode && (
          <div className="w-1/2 border-l border-gray-200">
            <div className="bg-gray-800 text-white p-4 text-sm font-mono overflow-auto" style={{ height: dimensions.height }}>
              <pre className="whitespace-pre-wrap">
                <code>{createEmailTemplate(sanitizedHtml)}</code>
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Preview Footer */}
      <div className="bg-gray-50 border-t border-gray-200 p-3">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <span>Zeichen: {htmlContent.length}</span>
            <span>Wörter: {htmlContent.split(/\s+/).filter(word => word.length > 0).length}</span>
          </div>
          <div className="text-xs">
            Sichere HTML-Vorschau mit DOMPurify
          </div>
        </div>
      </div>
    </div>
  );
};

export default HtmlPreview;
